const MarkdownIt = require("markdown-it");

const viewerRoot = document.querySelector(".t47-viewer");
const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: false,
});

if (viewerRoot) {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("source") || viewerRoot.dataset.defaultSource;
  const recordId = params.get("id");
  const documentNode = viewerRoot.querySelector("[data-record-document]");
  let loadedRecord = null;

  loadRecord();

  async function loadRecord() {
    if (!source || !recordId) {
      documentNode.innerHTML = `<div class="usa-alert usa-alert--error"><div class="usa-alert__body"><p class="usa-alert__text">Missing record ID.</p></div></div>`;
      return;
    }
    documentNode.innerHTML = `<p>Loading record...</p>`;
    try {
      loadedRecord = await fetchJson(`/api/record/${encodeURIComponent(source)}/${encodeURIComponent(recordId)}`);
      renderRecord(loadedRecord);
    } catch (error) {
      documentNode.innerHTML = `<div class="usa-alert usa-alert--error"><div class="usa-alert__body"><p class="usa-alert__text">${escapeHtml(error.message)}</p></div></div>`;
    }
  }

  function renderRecord(payload) {
    const record = payload.record;
    const mediaItems = mediaFromRecord(record);
    const markdownSource = mediaItems.length
      ? stripMarkdownMediaSection(displayMarkdown(payload.markdown || ""))
      : displayMarkdown(payload.markdown || "");
    const renderedMarkdown = markdown.render(markdownWithMediaUrls(markdownSource, mediaItems));
    document.title = `${record.title || "Record"} | Transparency47`;
    documentNode.innerHTML = `
      <header class="t47-document__header">
        <p class="t47-record-card__meta">${escapeHtml(payload.sourceName)} · ${escapeHtml(record.category || "")} · ${escapeHtml(record.date || "No date")}</p>
        <h1>${escapeHtml(record.title || "Untitled record")}</h1>
        <dl class="t47-metadata-list">
          <div><dt>Source</dt><dd>${escapeHtml(payload.sourceName)}</dd></div>
          <div><dt>Kind</dt><dd>${escapeHtml(record.kind || "record")}</dd></div>
          <div><dt>Repo path</dt><dd>${escapeHtml(record.path || "")}</dd></div>
          ${record.sourceUrl ? `<div><dt>Original public source</dt><dd><a class="usa-link" href="${escapeHtml(record.sourceUrl)}">${escapeHtml(record.sourceUrl)}</a></dd></div>` : ""}
        </dl>
        ${payload.truncated ? `<div class="usa-alert usa-alert--warning usa-alert--slim"><div class="usa-alert__body"><p class="usa-alert__text">This record was truncated in the viewer because it exceeded the current size limit.</p></div></div>` : ""}
      </header>
      ${renderMedia(mediaItems)}
      <div class="t47-markdown-body">${renderedMarkdown}</div>
    `;
  }

  function mediaFromRecord(record) {
    const media = record?.metadata?.media;
    if (Array.isArray(media)) {
      return media.filter((item) => item && item.url);
    }
    const urls = record?.metadata?.mediaUrls;
    if (Array.isArray(urls)) {
      return urls
        .filter(Boolean)
        .map((url) => ({
          kind: mediaKindFromUrl(url),
          url,
        }));
    }

    const metadata = record?.metadata || {};
    const videoUrl = metadata.remoteUrl || metadata.downloadUrl;
    if (!videoUrl) return [];
    return [{
      kind: "video",
      url: videoUrl,
      poster: metadata.thumbnail || "",
      sourceUrl: record.sourceUrl || "",
    }];
  }

  function renderMedia(items) {
    if (!items.length) return "";
    const rendered = items.map((item, index) => {
      const label = `Attachment ${index + 1}`;
      if (item.kind === "video" || isVideoUrl(item.url)) {
        return `
          <figure class="t47-media-item t47-media-item--video">
            ${renderVideoPlayer(item, index)}
            <figcaption>${escapeHtml(label)}${item.sourceUrl ? ` · <a class="usa-link" href="${escapeHtml(item.sourceUrl)}">Original media source</a>` : ""}</figcaption>
          </figure>
        `;
      }
      if (item.kind === "image" || isImageUrl(item.url)) {
        return `
          <figure class="t47-media-item t47-media-item--image">
            <img src="${escapeHtml(item.url)}" alt="${escapeHtml(label)}">
            <figcaption>${escapeHtml(label)}${item.sourceUrl ? ` · <a class="usa-link" href="${escapeHtml(item.sourceUrl)}">Original media source</a>` : ""}</figcaption>
          </figure>
        `;
      }
      return `
        <p class="t47-media-link"><a class="usa-link" href="${escapeHtml(item.url)}">${escapeHtml(label)}</a></p>
      `;
    }).join("");
    return `
      <section class="t47-media" aria-label="Record media">
        ${rendered}
      </section>
    `;
  }

  function renderVideoPlayer(item) {
    const poster = item.poster ? ` poster="${escapeHtml(item.poster)}"` : "";
    return `
      <video-player class="t47-video-player">
        <video-skin class="t47-video-skin">
          <video src="${escapeHtml(item.url)}" preload="metadata" playsinline controls${poster}></video>
        </video-skin>
      </video-player>
    `;
  }

  function markdownWithMediaUrls(value, mediaItems) {
    let next = value;
    for (const item of mediaItems) {
      if (!item.localFile || !item.url) continue;
      next = next.replaceAll(`](${item.localFile})`, `](${item.url})`);
      next = next.replaceAll(`](${encodeURI(item.localFile)})`, `](${item.url})`);
    }
    return next;
  }

  function displayMarkdown(value) {
    return String(value || "").replace(/<!--[\s\S]*?-->\s*/g, "");
  }

  function stripMarkdownMediaSection(value) {
    return String(value || "").replace(/\n## Media\s*\n[\s\S]*?(?=\n## |\n# |$)/i, "\n");
  }

  function mediaKindFromUrl(url) {
    if (isVideoUrl(url)) return "video";
    if (isImageUrl(url)) return "image";
    return "media";
  }

  function isVideoUrl(url) {
    return /\.(mp4|webm|mov|m4v)(?:[?#].*)?$/i.test(String(url || ""));
  }

  function isImageUrl(url) {
    return /\.(png|jpe?g|gif|webp|avif)(?:[?#].*)?$/i.test(String(url || ""));
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Request failed with status ${response.status}`);
  }
  return payload;
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
